import { useQuery } from "@tanstack/react-query";
import { FileDown, Lock, FlaskConical, Stamp, Leaf } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { ScamWarning } from "@/components/site/ScamWarning";
import { api, API } from "@/lib/api";

const ITEMS = [
  { icon: Stamp, t: "APEDA registration", d: "Registered exporter with the Agricultural and Processed Food Products Export Development Authority. The certificate is public below." },
  { icon: Leaf, t: "Phytosanitary certificate", d: "Issued by the Plant Quarantine authority for every shipment. Sample copies are shared by secure link after you inquire." },
  { icon: FlaskConical, t: "Lab and residue reports", d: "Pesticide residue (MRL) reports from accredited labs, shared on request for your market's limits." },
];

const PublicDocs = () => {
  const { data: docs = [] } = useQuery({ queryKey: ["public-docs"], queryFn: async () => (await api.get("/documents/public")).data });
  if (!docs.length) return <p className="text-sm text-stone-500" data-testid="public-docs-empty">The APEDA registration certificate will be available here shortly.</p>;
  return (
    <ul className="divide-y divide-stone-200 rounded-lg border border-stone-200 bg-white" data-testid="public-docs-list">
      {docs.map((d) => (
        <li key={d.id} className="flex items-center justify-between gap-4 p-5">
          <div>
            <p className="font-medium">{d.title}</p>
            <p className="text-xs text-stone-500">{d.doc_type}</p>
          </div>
          <a href={`${API}/documents/public/${d.id}/download`} data-testid={`public-doc-download-${d.id}`}
            className="inline-flex items-center gap-2 rounded-full bg-emerald-900 hover:bg-emerald-800 text-white px-4 py-2 text-sm transition-colors">
            <FileDown className="h-4 w-4" /> Download
          </a>
        </li>
      ))}
    </ul>
  );
};

export default function Quality() {
  return (
    <>
      <Seo title="Quality & Compliance" description="APEDA registration, phytosanitary certificates and residue lab reports for SLN Agro Exports shipments." path="/quality-compliance" />
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 space-y-16">
        <div className="max-w-2xl space-y-4">
          <p className="text-xs uppercase tracking-[0.25em] text-emerald-800">Quality & Compliance</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight">Paperwork you can check.</h1>
          <p className="text-stone-600">Our registration is public. Shipment documents are private and shared with you by a link that expires after 7 days.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {ITEMS.map((i) => (
            <div key={i.t} className="rounded-lg border border-stone-200 bg-white p-8 space-y-3">
              <i.icon className="h-6 w-6 text-emerald-800" />
              <h3 className="text-xl font-semibold">{i.t}</h3>
              <p className="text-sm text-stone-600 leading-relaxed">{i.d}</p>
            </div>
          ))}
        </div>
        <p className="text-sm text-stone-500 border-l-2 border-amber-600 pl-4" data-testid="quality-placeholder">[List any other certifications you hold, e.g. GlobalG.A.P., FSSAI, packhouse registration, with their numbers.]</p>
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-7 space-y-5">
            <h2 className="text-2xl lg:text-3xl font-semibold tracking-tight">Public documents</h2>
            <PublicDocs />
          </div>
          <div className="md:col-span-5 rounded-lg bg-emerald-50 border border-emerald-100 p-8 space-y-3">
            <Lock className="h-5 w-5 text-emerald-800" />
            <h3 className="text-lg font-semibold text-emerald-950">Need phytosanitary or lab reports?</h3>
            <p className="text-sm text-stone-700">Send a quote request or ask on WhatsApp. We send you a private download link valid for 7 days.</p>
          </div>
        </div>
        <ScamWarning />
      </section>
    </>
  );
}
